import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  DialogContentText,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import { AccessTime, SaveOutlined, Tune } from '@mui/icons-material';
import ScheduleCard from '../../../components/employee/services/ScheduleCard';
import { convertTo12Hour, formatDate } from '../../../time/timeUtils';
import { DAYS_OF_WEEK_PREFERRED_ORDER, MAX_WORK_HOURS_UPDATE_RANGE_DAYS } from '../../../constants';
import ConfirmationDialog from '../../../components/global/ConfirmationDialog';
import { EditWorkHoursViewModel } from './EditWorkHoursViewModel';
import SuccessOrErrorDialog from '../../../components/global/SuccessOrErrorDialog';
import { Dayjs } from '../../../utils/dayjs-config';
import LoadingSpinner from '../../../components/global/LoadingSpinner';

type EditWorkHoursPageProps = {
  requireBookableAvailability?: boolean;
  embedded?: boolean;
};

const EditWorkHoursPage: React.FC<EditWorkHoursPageProps> = ({
  requireBookableAvailability = false,
  embedded = false,
}) => {
  const {
    parsedSchedule,
    dialogOpen,
    setDialogOpen,
    affectedAppointments,
    startDate,
    endDate,
    makeDefaultSchedule,
    resultDialogOpen,
    onCloseResultDialog,
    onSaveMutation,
    handleScheduleChange,
    recurringBreaksByDay,
    handleRecurringBreaksChange,
    onStartDateChange,
    onEndDateChange,
    onMakeDefaultScheduleChange,
    isLoading,
    isSetUpError,
    errorMessage,
    isSaving,
    saveValidationError,
    saveErrorMessage,
  } = EditWorkHoursViewModel({ requireBookableAvailability });

  if (isLoading) return <LoadingSpinner label="Loading hours" />;

  if (isSetUpError) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">
          <AlertTitle>Work hours could not be loaded</AlertTitle>
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  const shouldDisableStartDate = (date: Dayjs): boolean => {
    if (date.isAfter(endDate)) return true;
    return endDate.diff(date, 'day') > MAX_WORK_HOURS_UPDATE_RANGE_DAYS;
  };

  const shouldDisableEndDate = (date: Dayjs): boolean => {
    if (date.isBefore(startDate)) return true;
    return date.diff(startDate, 'day') > MAX_WORK_HOURS_UPDATE_RANGE_DAYS;
  };

  const confirmationMessage = makeDefaultSchedule
    ? `This will set your updated work hours starting on ${startDate.format('MM/DD/YYYY')} and keep them as your default schedule moving forward.`
    : `This will set your updated work hours from ${startDate.format('MM/DD/YYYY')} through ${endDate.format('MM/DD/YYYY')}.`;

  return (
    <Box
      sx={{
        minHeight: '100%',
        pb: embedded ? 0 : 8,
        bgcolor: '#fff',
      }}
    >
      <Container maxWidth="lg" sx={{ pt: embedded ? 0 : { xs: 2, md: 4 } }}>
        {!embedded ? <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
          sx={{
            mb: 3,
            pb: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: 'secondary.dark',
              bgcolor: 'rgba(25,118,210,.1)',
            }}
          >
            <AccessTime />
          </Box>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 850 }}>
              Work hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Set weekly availability, breaks, and effective dates.
            </Typography>
          </Box>
        </Stack> : null}

        <Grid2 container spacing={3} alignItems="flex-start">
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Paper
              variant="outlined"
              sx={{ borderRadius: 3.5, overflow: 'hidden', borderColor: 'divider' }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
                sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}
              >
                <Box>
                  <Typography variant="h6">Weekly availability</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Turn on the days you work, then choose your booking window and optional break.
                  </Typography>
                </Box>
              </Stack>
              <Divider />
              <Stack spacing={1.5} sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                {Object.keys(parsedSchedule).length > 0 && DAYS_OF_WEEK_PREFERRED_ORDER.map((day) => (
                  <ScheduleCard
                    key={day}
                    day={day}
                    initialWorkHours={parsedSchedule[day]}
                    onScheduleChange={handleScheduleChange}
                    recurringBreaks={recurringBreaksByDay[day]}
                    onRecurringBreaksChange={handleRecurringBreaksChange}
                    templateEndDate={endDate}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3.5,
                borderColor: 'divider',
                position: { md: 'sticky' },
                top: { md: 24 },
                boxShadow: '0 12px 30px rgba(16, 24, 40, 0.07)',
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.75 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(25,118,210,.1)',
                    color: 'secondary.dark',
                  }}
                >
                  <Tune fontSize="small" />
                </Box>
                <Typography variant="h6">Apply schedule</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                Choose when these hours begin and whether they should remain your default.
              </Typography>

              <DatePicker
                label="Starts on"
                value={startDate}
                onChange={(date) => date && onStartDateChange(date)}
                disablePast
                shouldDisableDate={shouldDisableStartDate}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />

              <Box
                sx={{
                  my: 2,
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: makeDefaultSchedule ? 'primary.main' : 'divider',
                  bgcolor: makeDefaultSchedule ? 'action.hover' : 'background.paper',
                }}
              >
                <FormControlLabel
                  sx={{ m: 0, width: '100%', alignItems: 'flex-start' }}
                  control={(
                    <Switch
                      checked={makeDefaultSchedule}
                      onChange={(event) => onMakeDefaultScheduleChange(event.target.checked)}
                      disabled={requireBookableAvailability}
                      sx={{ mr: 0.5 }}
                    />
                  )}
                  label={(
                    <Box sx={{ pt: 0.5 }}>
                      <Typography variant="subtitle2">Keep as my default</Typography>
                      <Typography variant="caption" color="text.secondary" component="div">
                        {requireBookableAvailability
                          ? 'Required during launch setup so customers have ongoing availability.'
                          : 'Use these hours for future availability until you change them again.'}
                      </Typography>
                    </Box>
                  )}
                />
              </Box>

              {!makeDefaultSchedule && (
                <Stack spacing={1} sx={{ mb: 2 }}>
                  <DatePicker
                    label="Ends on"
                    value={endDate}
                    onChange={(date) => date && onEndDateChange(date)}
                    disablePast
                    shouldDisableDate={shouldDisableEndDate}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Date ranges can cover up to {MAX_WORK_HOURS_UPDATE_RANGE_DAYS} days.
                  </Typography>
                </Stack>
              )}

              {saveValidationError && (
                <Alert severity="error" sx={{ mb: 2 }}>{saveValidationError}</Alert>
              )}

              {affectedAppointments.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <AlertTitle>Appointments affected</AlertTitle>
                  Saving will cancel {affectedAppointments.length} appointment{affectedAppointments.length === 1 ? '' : 's'} that no longer fit these hours.
                </Alert>
              )}

              <Button
                fullWidth
                size="large"
                variant="contained"
                color="secondary"
                startIcon={<SaveOutlined />}
                onClick={() => setDialogOpen(true)}
                disabled={isSaving || Boolean(saveValidationError)}
                sx={{ minHeight: 48, borderRadius: 2.5, fontWeight: 800, textTransform: 'none' }}
              >
                {isSaving ? 'Saving schedule...' : 'Save work hours'}
              </Button>
            </Paper>
          </Grid2>
        </Grid2>

        <ConfirmationDialog
          open={dialogOpen}
          title="Confirm work hours"
          isLoading={isSaving}
          loadingLabel="Saving..."
          content={affectedAppointments.length > 0 ? (
            <>
              <DialogContentText sx={{ mt: 2 }}>
                These appointments no longer fit your availability and will be cancelled:
              </DialogContentText>
              <Box component="ul" sx={{ pl: 2.5 }}>
                {affectedAppointments.map((appointment) => (
                  <Box component="li" key={appointment.id} sx={{ mb: 0.75 }}>
                    <Typography variant="body2">
                      {appointment.appointmentTitle} with {appointment.customerFirstName} at{' '}
                      {convertTo12Hour(appointment.appointmentTime)} on {formatDate(appointment.appointmentDate)}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <DialogContentText>
                Close this dialog if you want to reschedule those appointments first.
              </DialogContentText>
            </>
          ) : undefined}
          onConfirm={() => {
            if (!isSaving) onSaveMutation.mutate();
          }}
          onClose={() => setDialogOpen(false)}
          message={confirmationMessage}
        />
        <SuccessOrErrorDialog
          open={resultDialogOpen}
          onClose={onCloseResultDialog}
          success={onSaveMutation.isSuccess}
          successTitle="Work hours updated"
          successMessage="Your availability and future schedule have been updated."
          errorTitle="Work hours could not be saved"
          errorMessage={saveErrorMessage}
        />
      </Container>
    </Box>
  );
};

export default EditWorkHoursPage;
